import { supabase } from '../lib/supabase';
import { Coach } from '../types';

type CoachSquadJoin = { squad_id: string; squads: { name: string; display_name: string } };
type CoachSquadRow = { coach_id: string; coaches: { id: string; name: string; address: string; phone: string; profile_image: string | null } };

export const getCoaches = async (): Promise<Coach[]> => {
  try {
    const { data: coaches, error } = await supabase
      .from('coaches')
      .select(`
        *,
        coach_squads!inner (
          squad_id,
          squads!inner (
            name,
            display_name
          )
        )
      `);

    if (error) {
      console.error('Error fetching coaches:', error);
      throw error;
    }

    if (!coaches) {
      return [];
    }

    // Transform the data to match our Coach type
    return coaches.map(coach => ({
      id: coach.id,
      name: coach.name,
      address: coach.address,
      phone: coach.phone,
      profileImage: coach.profile_image,
      squads: (coach.coach_squads as CoachSquadJoin[] | null)?.map(cs => cs.squads.name as Coach['squads'][number]) ?? []
    }));
  } catch (error) {
    console.error('Error in getCoaches:', error);
    throw error;
  }
};

export const createCoach = async (coach: Omit<Coach, 'id'>): Promise<Coach> => {
  try {
    // First, insert the coach
    const { data: newCoach, error: coachError } = await supabase
      .from('coaches')
      .insert({
        name: coach.name,
        address: coach.address,
        phone: coach.phone,
        profile_image: coach.profileImage
      })
      .select()
      .single();

    if (coachError) {
      console.error('Error creating coach:', coachError);
      throw coachError;
    }

    if (!newCoach) {
      throw new Error('No coach data returned after creation');
    }

    // Get squad IDs
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id, name')
      .in('name', coach.squads);

    if (squadError) {
      console.error('Error fetching squad IDs:', squadError);
      throw squadError;
    }

    // Then, insert the coach's squads
    if (squadData && squadData.length > 0) {
      const squadInserts = squadData.map(squad => ({
        coach_id: newCoach.id,
        squad_id: squad.id
      }));

      const { error: squadsError } = await supabase
        .from('coach_squads')
        .insert(squadInserts);

      if (squadsError) {
        console.error('Error creating coach squads:', squadsError);
        throw squadsError;
      }
    }

    // Return the new coach with squads
    return {
      id: newCoach.id,
      name: newCoach.name,
      address: newCoach.address,
      phone: newCoach.phone,
      profileImage: newCoach.profile_image,
      squads: coach.squads
    };
  } catch (error) {
    console.error('Error in createCoach:', error);
    throw error;
  }
};

export const updateCoach = async (id: string, coach: Omit<Coach, 'id'>): Promise<Coach> => {
  try {
    // First, update the coach
    const { data: updatedCoach, error: coachError } = await supabase
      .from('coaches')
      .update({
        name: coach.name,
        address: coach.address,
        phone: coach.phone,
        profile_image: coach.profileImage
      })
      .eq('id', id)
      .select()
      .single();

    if (coachError) {
      console.error('Error updating coach:', coachError);
      throw coachError;
    }

    if (!updatedCoach) {
      throw new Error('No coach data returned after update');
    }

    // Get squad IDs
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id, name')
      .in('name', coach.squads);

    if (squadError) {
      console.error('Error fetching squad IDs:', squadError);
      throw squadError;
    }

    // Delete existing squad associations
    const { error: deleteError } = await supabase
      .from('coach_squads')
      .delete()
      .eq('coach_id', id);

    if (deleteError) {
      console.error('Error deleting coach squads:', deleteError);
      throw deleteError;
    }

    // Insert new squad associations
    if (squadData && squadData.length > 0) {
      const squadInserts = squadData.map(squad => ({
        coach_id: id,
        squad_id: squad.id
      }));

      const { error: squadsError } = await supabase
        .from('coach_squads')
        .insert(squadInserts);

      if (squadsError) {
        console.error('Error updating coach squads:', squadsError);
        throw squadsError;
      }
    }

    // Return the updated coach with squads
    return {
      id,
      name: updatedCoach.name,
      address: updatedCoach.address,
      phone: updatedCoach.phone,
      profileImage: updatedCoach.profile_image,
      squads: coach.squads
    };
  } catch (error) {
    console.error('Error in updateCoach:', error);
    throw error;
  }
};

export const deleteCoach = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('coaches')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting coach:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteCoach:', error);
    throw error;
  }
};

export const getCoachesBySquad = async (squad: string): Promise<Coach[]> => {
  try {
    // First get the squad ID
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id')
      .eq('name', squad)
      .single();

    if (squadError) {
      console.error('Error fetching squad ID:', squadError);
      throw squadError;
    }

    if (!squadData) {
      throw new Error(`Squad not found: ${squad}`);
    }

    const { data, error } = await supabase
      .from('coach_squads')
      .select(`
        coach_id,
        coaches!inner (*)
      `)
      .eq('squad_id', squadData.id);

    if (error) {
      console.error('Error fetching coaches by squad:', error);
      throw error;
    }

    if (!data) {
      return [];
    }

    // Transform the data to match our Coach type
    return (data as CoachSquadRow[]).map(item => ({
      id: item.coaches.id,
      name: item.coaches.name,
      address: item.coaches.address,
      phone: item.coaches.phone,
      profileImage: item.coaches.profile_image ?? undefined,
      squads: [squad as Coach['squads'][number]]
    }));
  } catch (error) {
    console.error('Error in getCoachesBySquad:', error);
    throw error;
  }
};